import React from "react";

const PIE_SIZE = 220;
const PIE_RADIUS = 88;
const PIE_INNER = 52;

function polarToCartesian(cx, cy, radius, angleRad) {
  return {
    x: cx + radius * Math.cos(angleRad),
    y: cy + radius * Math.sin(angleRad),
  };
}

function describeArc(cx, cy, radius, startAngle, endAngle) {
  const start = polarToCartesian(cx, cy, radius, endAngle);
  const end = polarToCartesian(cx, cy, radius, startAngle);
  const largeArc = endAngle - startAngle <= Math.PI ? 0 : 1;
  return `M ${start.x} ${start.y} A ${radius} ${radius} 0 ${largeArc} 0 ${end.x} ${end.y} L ${cx} ${cy} Z`;
}

export function BudgetPieChart({ data, formatValue }) {
  const slices = Array.isArray(data) ? data.filter((d) => d.value > 0) : [];
  if (!slices.length) {
    return <div className="reporting-chart-empty">No budget data for the current filters.</div>;
  }

  const cx = PIE_SIZE / 2;
  const cy = PIE_SIZE / 2;
  let cursor = -Math.PI / 2;

  const paths = slices.map((slice) => {
    const angle = slice.pct * Math.PI * 2;
    const start = cursor;
    const end = cursor + angle;
    cursor = end;
    return {
      ...slice,
      d: describeArc(cx, cy, PIE_RADIUS, start, end),
    };
  });

  return (
    <div className="reporting-pie-wrap">
      <svg
        className="reporting-pie-svg"
        viewBox={`0 0 ${PIE_SIZE} ${PIE_SIZE}`}
        role="img"
        aria-label="Budget distribution pie chart"
      >
        {paths.map((slice) => (
          <path
            key={slice.name}
            d={slice.d}
            fill={slice.color}
            stroke="#fff"
            strokeWidth="1.5"
            opacity={slice.faded ? 0.45 : 1}
          >
            <title>
              {slice.name}: {formatValue(slice.value)} ({Math.round(slice.pct * 100)}%)
            </title>
          </path>
        ))}
        <circle cx={cx} cy={cy} r={PIE_INNER} fill="#fff" />
      </svg>
      <ul className="reporting-chart-legend">
        {slices.map((slice) => (
          <li key={slice.name} className={slice.faded ? "is-faded" : undefined}>
            <span
              className="reporting-chart-swatch"
              style={{ background: slice.color, opacity: slice.faded ? 0.55 : 1 }}
              aria-hidden
            />
            <span className="reporting-chart-legend-label">{slice.name}</span>
            <span className="reporting-chart-legend-value">{formatValue(slice.value)}</span>
          </li>
        ))}
      </ul>
    </div>
  );
}

export function BudgetBarChart({ data, formatValue, showProgressPercent = false }) {
  const bars = Array.isArray(data) ? data : [];
  if (!bars.length) {
    return <div className="reporting-chart-empty">No budget data for the current filters.</div>;
  }

  const max = Math.max(...bars.map((b) => Math.abs(b.value)), 1);

  return (
    <div className="reporting-bar-chart">
      {bars.map((bar) => {
        const widthPct =
          bar.barPercent != null ? bar.barPercent : (Math.abs(bar.value) / max) * 100;
        const progressLabel =
          showProgressPercent && bar.barPercent != null
            ? ` (${Math.round(bar.barPercent)}%)`
            : "";
        return (
          <div
            className={`reporting-bar-row${bar.faded ? " is-faded" : ""}`}
            key={bar.name}
          >
            <div className="reporting-bar-label" title={bar.name}>
              {bar.name}
            </div>
            <div
              className="reporting-bar-track"
              aria-hidden
              title={
                showProgressPercent && bar.barPercent != null
                  ? `${Math.round(bar.barPercent)}% complete`
                  : undefined
              }
            >
              <div
                className="reporting-bar-fill"
                style={{
                  width: `${widthPct}%`,
                  background: bar.color,
                  opacity: bar.faded ? 0.45 : 1,
                }}
              />
            </div>
            <div className="reporting-bar-value">
              {formatValue(bar.value)}
              {progressLabel ? <span className="reporting-bar-pct">{progressLabel}</span> : null}
            </div>
          </div>
        );
      })}
    </div>
  );
}
